import {type FC, useEffect, useRef} from 'react';

import {Observer} from 'mobx-react';

import {pollAsync} from '@yourcoach/async-utils';
import {useI18n} from '@yourcoach/i18n';

import type {ThemeAndAnyColor} from '../../../../styles';
import type {Ref as TextInputRef} from '../../../../uikit/TextInput/types';
import type {ConferenceRatingStore} from '../../stores/ConferenceRating';
import type {RateConferenceConfig} from '../../types';

import {getSessionRateAnimation} from '../../../../assets/animations/session-rate';
import {RatingBar} from '../../../../uikit/RatingBar';
import {Text} from '../../../../uikit/Text';
import {View} from '../../../../uikit/View';
import {useIsKeyboardOpen} from '../../../../utils/hooks/useIsKeyboardOpen';
import {logger} from '../../../../utils/logger';
import {platform} from '../../../../utils/platform';
import * as S from './styles';

type Props = Omit<RateConferenceConfig, 'id'> & {
  store: ConferenceRatingStore;
};

export const ConferenceRating: FC<Props> = ({isB2b, role, store, type}) => {
  const {t} = useI18n();
  const commentInputRef = useRef<TextInputRef>(null);

  const {isKeyboardOpenRef} = useIsKeyboardOpen();

  useEffect(() => {
    logger.event('session_rate_view', {
      role,
      session_type: type,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    store.setConfig({
      isB2b,
      role,
      type,
    });
  }, [store, role, type, isB2b]);

  const DEFAULT_LABELS = {
    1: t('shared:poor'),
    4: t('shared:neutral'),
    7: t('shared:excellent'),
  };

  const getActiveColor = () => {
    if (role === 'coach') {
      return 'global1';
    } else {
      return 'global2';
    }
  };

  const renderAudioQualityBar = () => (
    <RatingBar
      activeColor={getActiveColor()}
      labels={DEFAULT_LABELS}
      max={7}
      onChange={value => store.setAudioQuality(value)}
      title={t('shared:audio_quality')}
    />
  );

  const renderVideoQualityBar = () => (
    <RatingBar
      activeColor={getActiveColor()}
      labels={DEFAULT_LABELS}
      max={7}
      onChange={value => store.setVideoQuality(value)}
      title={t('shared:video_quality')}
    />
  );

  const renderCoachingQualityBar = () => (
    <RatingBar
      activeColor={getActiveColor()}
      labels={DEFAULT_LABELS}
      max={7}
      onChange={value => store.setCoachingQuality(value)}
      title={t('shared:coaching_session_quality')}
    />
  );

  const onCommentInputFocus = async () => {
    if (platform.isMobileBrowser) {
      if (!isKeyboardOpenRef.current) {
        await pollAsync(() => isKeyboardOpenRef.current);
      }

      commentInputRef.current?.ref.web?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const renderComment = () => (
    <S.TextAreaWrap>
      <Text weight="semibold">{t('shared:feedback_additional_comments')}</Text>
      <Observer>
        {() => (
          <S.CommentInput
            error={
              store.shouldShowCommentError
                ? t('shared:share_session_issue')
                : undefined
            }
            onChange={({payload}) => {
              store.setComment(payload?.text ?? null);
            }}
            onFocus={onCommentInputFocus}
            placeholder={t('shared:leave_your_comments_here')}
            ref={commentInputRef}
          />
        )}
      </Observer>
    </S.TextAreaWrap>
  );

  const renderForCoachLiveB2b = () => (
    <>
      {renderAudioQualityBar()}
      {renderVideoQualityBar()}
      {renderComment()}
    </>
  );

  const renderForClientLiveB2b = () => (
    <>
      {renderAudioQualityBar()}
      {renderVideoQualityBar()}
      {renderCoachingQualityBar()}
      {renderComment()}
    </>
  );

  const renderForCoachLiveCore = () => (
    <>
      {renderAudioQualityBar()}
      {renderVideoQualityBar()}
      {renderComment()}
    </>
  );

  const renderForClientLiveCore = () => (
    <>
      {renderAudioQualityBar()}
      {renderVideoQualityBar()}
      {renderComment()}
    </>
  );

  const renderForClientChat = () => (
    <>
      {renderCoachingQualityBar()}
      {renderComment()}
    </>
  );

  const getAnimationColor = (): ThemeAndAnyColor => {
    switch (true) {
      case role === 'coach':
        return 'global1';
      default:
        return 'global2';
    }
  };

  const renderForLive = () => {
    switch (role) {
      case 'client':
        return isB2b ? renderForClientLiveB2b() : renderForClientLiveCore();
      case 'coach':
        return isB2b ? renderForCoachLiveB2b() : renderForCoachLiveCore();
      default:
        return null;
    }
  };

  const renderForChat = () => {
    switch (role) {
      case 'client':
        return renderForClientChat();
      default:
        logger.error(
          new Error('Attemt to render coach UI for rate chat session'),
        );

        return null;
    }
  };

  const getRatingVariant = () => {
    switch (type) {
      case 'chat':
        return renderForChat();
      case 'live':
        return renderForLive();
      default:
        return '';
    }
  };

  const ratingVariant = getRatingVariant();

  const renderSessionFeedbackText = () =>
    role === 'client' ? (
      <S.ExplanationText>
        {t('shared:feedback_live_session.feedback_is_for_quality')}
      </S.ExplanationText>
    ) : null;

  return (
    <S.Container>
      <View />
      <S.Animation
        autoPlay={true}
        colorify={{
          color: getAnimationColor(),
        }}
        loop={false}
        source={getSessionRateAnimation}
        speed={1.5}
      />
      <S.Title>
        {
          /**
           * @i18n_parser
           *
           * t('shared:feedback_live_session.live')
           * t('shared:feedback_live_session.chat')
           */
          t(`shared:feedback_live_session.${type}`)
        }
      </S.Title>
      {renderSessionFeedbackText()}
      {ratingVariant}
    </S.Container>
  );
};
